/**
 * OnboardingView — multi-step self-onboarding wizard.
 *
 * S-059 / S-060 / FEAT-006 (Phase 3). Lands a fresh Clerk user with a
 * usable tenant after sign-up. The four steps are:
 *
 *   1. Welcome — brief copy + Clerk <CreateOrganization> if the user
 *      has no active org yet (option (a) per the spec handoff). The
 *      Clerk org becomes the platform tenant.
 *   2. Workspace — single text input → creates the first `agencyclient`
 *      row (`POST /agencyclients`).
 *   3. Publication — single text input → creates the first `publications`
 *      row. The publication FK chain requires a voice_profile and a
 *      brand_identity; we silently create minimal defaults for both so
 *      the user is not asked to fill long editorial DNA forms during
 *      first-run. They can edit later in Settings.
 *   4. API key — password input for the Anthropic BYOAPI key
 *      (`POST /api-keys`, provider="anthropic", agencyclient_id=null →
 *      tenant scope). "Skip" leaves the api_key step out of the
 *      sequence entirely.
 *
 * On final submit the wizard runs steps 2 → 4 sequentially via
 * `useApi`. Mid-sequence failure: the wizard keeps form state, surfaces
 * the offending HTTP status + body inline, and tracks which step
 * succeeded so the user can press "Retry" without double-creating any
 * row.
 *
 * On full success: a sessionStorage flag (`ccm.welcomeToast`) is set
 * and the wizard navigates to /kanban; the Kanban view (later phase)
 * is responsible for surfacing that flag as a one-time toast. The flag
 * is consumed and cleared on read.
 *
 * The Shell-level tenant check (see `RequireTenant.tsx`) drives the
 * redirect that puts users on this screen in the first place.
 */

import { useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreateOrganization,
  useAuth,
  useClerk,
  useOrganization,
} from "@clerk/clerk-react";
import { ApiError, useApi } from "@/lib/api";

// ---------- Types ----------

type StepIndex = 0 | 1 | 2 | 3;

interface FormState {
  step: StepIndex;
  workspaceName: string;
  publicationName: string;
  apiKey: string;
  skipApiKey: boolean;
  // Track what's already been created server-side so a retry after a
  // mid-sequence failure can pick up where it left off rather than
  // double-creating rows.
  // S-075: tenantCreated tracks the POST /tenants call separately from
  // the agencyclient/publication chain. The tenant call is what bridges
  // a fresh user (tenant_id=null in JWT) into a usable tenant context;
  // its 409 / 201 outcomes are both idempotent successes.
  tenantCreated: boolean;
  createdAgencyclientId: number | null;
  createdPublicationId: number | null;
  createdVoiceProfileId: number | null;
  createdBrandIdentityId: number | null;
  apiKeySaved: boolean;
}

type Action =
  | { type: "set"; field: keyof FormState; value: FormState[keyof FormState] }
  | { type: "advance" }
  | { type: "back" }
  | { type: "ackTenant" }
  | { type: "ackAgencyclient"; id: number }
  | { type: "ackVoiceProfile"; id: number }
  | { type: "ackBrandIdentity"; id: number }
  | { type: "ackPublication"; id: number }
  | { type: "ackApiKey" };

const initialState: FormState = {
  step: 0,
  workspaceName: "",
  publicationName: "",
  apiKey: "",
  skipApiKey: false,
  tenantCreated: false,
  createdAgencyclientId: null,
  createdPublicationId: null,
  createdVoiceProfileId: null,
  createdBrandIdentityId: null,
  apiKeySaved: false,
};

function reducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case "set":
      return { ...state, [action.field]: action.value };
    case "advance":
      return state.step < 3 ? { ...state, step: (state.step + 1) as StepIndex } : state;
    case "back":
      return state.step > 0 ? { ...state, step: (state.step - 1) as StepIndex } : state;
    case "ackTenant":
      return { ...state, tenantCreated: true };
    case "ackAgencyclient":
      return { ...state, createdAgencyclientId: action.id };
    case "ackVoiceProfile":
      return { ...state, createdVoiceProfileId: action.id };
    case "ackBrandIdentity":
      return { ...state, createdBrandIdentityId: action.id };
    case "ackPublication":
      return { ...state, createdPublicationId: action.id };
    case "ackApiKey":
      return { ...state, apiKeySaved: true };
  }
}

// ---------- Provisioning sequence ----------

interface TenantRow { id: number; clerkOrgId: string; name: string }
interface AgencyclientRow { id: number; name: string; slug: string }
/**
 * POST /tenants returns the tenant + the default agencyclient created
 * alongside it. The wizard renames that agencyclient in-place instead
 * of creating a second one — the JWT is scoped to it and the RLS
 * policies on downstream tables only allow inserts under it.
 */
interface CreateTenantResponse {
  tenant: TenantRow;
  agencyclient: AgencyclientRow;
}
interface VoiceProfileRow { id: number }
interface BrandIdentityRow { id: number }
interface PublicationRow { id: number; name: string }

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "workspace";
}

// ---------- Component ----------

export function OnboardingView() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const api = useApi();
  const { getToken } = useAuth();
  const clerk = useClerk();
  const { organization, isLoaded: orgLoaded } = useOrganization();

  const stepLabels = ["Welcome", "Workspace", "Publication", "API Key"] as const;

  const canAdvanceWelcome = orgLoaded && !!organization;
  const canAdvanceWorkspace = state.workspaceName.trim().length > 0;
  const canAdvancePublication = state.publicationName.trim().length > 0;
  const canFinish = state.skipApiKey || state.apiKey.trim().length > 0;

  // Run the create sequence end-to-end. Idempotent against prior partial
  // success — each step is skipped if the corresponding `ackX` already
  // recorded an ID in state.
  async function runProvisioning() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Step 0 — tenant (S-075). MUST run first: a fresh Clerk user has
      // tenant_id=null in their JWT, which makes /agencyclients (and every
      // RLS-gated route) 401 with `JWT is missing tenant_id`. POST /tenants
      // mints the row + syncs publicMetadata.tenant_db_id back to Clerk, so
      // the next minted JWT carries the full claim set. Both 201 (created)
      // and 409 (already exists, returned existing row) are idempotent
      // successes here.
      let agencyclientId = state.createdAgencyclientId;

      if (!state.tenantCreated) {
        // Belt-and-suspenders: explicitly set the just-created org as the
        // active session org. Clerk's <CreateOrganization> does not always
        // scope the session to the new org for first-time users, and our
        // POST /tenants reads org_id from the JWT claims.
        if (organization) {
          try {
            await clerk.setActive({ organization: organization.id });
          } catch {
            // non-fatal — getToken({skipCache}) below will surface a real
            // mismatch as a 401 from POST /tenants if scoping really failed.
          }
        }
        // Force-refresh the cached Clerk JWT BEFORE POST /tenants so the
        // new token carries the org_id claim. The cached token was minted
        // at sign-in (pre-<CreateOrganization>) and would lack org_id,
        // producing a 401 no_org_context that strands the user.
        try {
          await getToken({ template: "ccm-default", skipCache: true });
        } catch {
          // non-fatal — POST below will surface a real refresh failure.
        }

        try {
          const created = await api<CreateTenantResponse>("/tenants", {
            method: "POST",
            body: JSON.stringify({ name: state.workspaceName.trim() }),
          });
          // Capture the default agencyclient created alongside the tenant.
          // We'll rename it below rather than creating a second one.
          agencyclientId = created.agencyclient.id;
          dispatch({ type: "ackAgencyclient", id: agencyclientId });
        } catch (err) {
          // 409 = tenant already exists for this Clerk org. The 409 body
          // is { tenant } only (no agencyclient). The user is mid-retry on
          // a half-finished onboarding — fall through and let the next
          // step (workspace rename) discover or reuse the agencyclient.
          if (err instanceof ApiError && err.status === 409) {
            // fallthrough — tenant exists, continue
          } else if (err instanceof ApiError && err.status === 401) {
            // no_org_context — the user hasn't completed <CreateOrganization>
            // yet. Send them back to step 0 with a clear hint.
            setSubmitError(
              "No active Clerk organization. Go back to step 1 and finish the workspace setup."
            );
            return;
          } else {
            throw err;
          }
        }
        dispatch({ type: "ackTenant" });

        // Force a fresh Clerk JWT so downstream calls see tenant_id +
        // agencyclient_id in claims. Clerk caches JWTs for ~60s by
        // default; without skipCache we'd 401 on the rename / voice
        // profile calls for up to a minute.
        try {
          await getToken({ template: "ccm-default", skipCache: true });
        } catch {
          // getToken failure here is non-fatal — the next api call will
          // refresh anyway. Swallow so we don't block the wizard.
        }
      }

      // Step A — rename the default agencyclient to the user's name.
      // POST /tenants created a "Default" agencyclient and set the user's
      // active_agencyclient_id to it. Creating a SECOND agencyclient here
      // would break RLS on all downstream inserts (the JWT remains scoped
      // to Default until active_agencyclient_id is updated + JWT refreshed).
      // Renaming in-place avoids the issue entirely and gives the user a
      // single, correctly-named workspace.
      if (!agencyclientId) {
        // Recovery: tenant was created on a previous attempt but we lost
        // the agencyclient id (page reload mid-flow). Look it up.
        // Filter on slug=default since POST /tenants always names it that.
        const list = await api<AgencyclientRow[]>("/agencyclients");
        const fallback =
          list.find((a) => a.slug === slugify(state.workspaceName)) ??
          list.find((a) => a.slug === "default") ??
          list[0];
        if (!fallback) {
          throw new Error(
            "No agencyclient found after tenant creation — provisioning is in an unrecoverable state"
          );
        }
        agencyclientId = fallback.id;
        dispatch({ type: "ackAgencyclient", id: agencyclientId });
      }
      // Idempotent: if the agencyclient already has the user's name, this
      // PATCH is a no-op. If it's still "Default", rename it.
      await api<AgencyclientRow>(`/agencyclients/${agencyclientId}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: state.workspaceName.trim(),
          slug: slugify(state.workspaceName),
        }),
      });

      // Step B — voice profile (default, hidden FK requirement of publication)
      let voiceProfileId = state.createdVoiceProfileId;
      if (!voiceProfileId) {
        const row = await api<VoiceProfileRow>("/voice-profiles", {
          method: "POST",
          body: JSON.stringify({
            agencyclient_id: agencyclientId,
            name: "Default voice",
            core_identity:
              "Default voice profile — edit in Settings to capture your publication's tone, hard rules, and litmus tests.",
            hard_rules: [],
          }),
        });
        voiceProfileId = row.id;
        dispatch({ type: "ackVoiceProfile", id: voiceProfileId });
      }

      // Step C — brand identity (default, hidden FK requirement of publication)
      let brandIdentityId = state.createdBrandIdentityId;
      if (!brandIdentityId) {
        const row = await api<BrandIdentityRow>("/brand-identities", {
          method: "POST",
          body: JSON.stringify({
            agencyclient_id: agencyclientId,
            name: "Default brand",
          }),
        });
        brandIdentityId = row.id;
        dispatch({ type: "ackBrandIdentity", id: brandIdentityId });
      }

      // Step D — publication
      if (!state.createdPublicationId) {
        const row = await api<PublicationRow>("/publications", {
          method: "POST",
          body: JSON.stringify({
            agencyclient_id: agencyclientId,
            slug: slugify(state.publicationName),
            name: state.publicationName.trim(),
            voice_profile_id: voiceProfileId,
            brand_identity_id: brandIdentityId,
          }),
        });
        dispatch({ type: "ackPublication", id: row.id });
      }

      // Step E — API key (optional)
      if (!state.skipApiKey && !state.apiKeySaved) {
        await api<{ id: string }>("/api-keys", {
          method: "POST",
          body: JSON.stringify({
            agencyclient_id: null,
            provider: "anthropic",
            key: state.apiKey,
          }),
        });
        dispatch({ type: "ackApiKey" });
      }

      // Flag a one-time welcome toast for Kanban to read on mount.
      try {
        window.sessionStorage.setItem("ccm.welcomeToast", "1");
      } catch {
        // sessionStorage can be blocked (private mode, certain extensions);
        // toast is non-critical, swallow.
      }

      navigate("/kanban", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(`HTTP ${err.status} — ${err.body || err.message}`);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Unknown error during provisioning");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-wrap">
      <div className="login-bg">
        <div className="login-bg-grid" />
      </div>
      <div className="login-card onboard-card">
        <div className="login-brand">
          <span className="brand-mark">◈</span>
          <span className="brand-name">nagareru</span>
        </div>

        <Stepper current={state.step} labels={stepLabels} />

        {state.step === 0 && (
          <WelcomeStep
            organizationLoaded={orgLoaded}
            hasOrganization={!!organization}
            onContinue={() => dispatch({ type: "advance" })}
            canContinue={canAdvanceWelcome}
          />
        )}
        {state.step === 1 && (
          <WorkspaceStep
            value={state.workspaceName}
            onChange={(v) => dispatch({ type: "set", field: "workspaceName", value: v })}
            onContinue={() => dispatch({ type: "advance" })}
            onBack={() => dispatch({ type: "back" })}
            canContinue={canAdvanceWorkspace}
          />
        )}
        {state.step === 2 && (
          <PublicationStep
            value={state.publicationName}
            onChange={(v) => dispatch({ type: "set", field: "publicationName", value: v })}
            onContinue={() => dispatch({ type: "advance" })}
            onBack={() => dispatch({ type: "back" })}
            canContinue={canAdvancePublication}
          />
        )}
        {state.step === 3 && (
          <ApiKeyStep
            value={state.apiKey}
            onChange={(v) => dispatch({ type: "set", field: "apiKey", value: v })}
            skip={state.skipApiKey}
            onToggleSkip={() =>
              dispatch({
                type: "set",
                field: "skipApiKey",
                value: !state.skipApiKey,
              })
            }
            onBack={() => dispatch({ type: "back" })}
            onFinish={runProvisioning}
            canFinish={canFinish && !submitting}
            submitting={submitting}
            error={submitError}
            progress={{
              workspace: state.createdAgencyclientId != null,
              publication: state.createdPublicationId != null,
              apiKey: state.apiKeySaved,
            }}
          />
        )}
      </div>
      <div className="login-side onboard-side">
        <div className="login-side-quote">
          <span className="login-side-mark">"</span>
          <p>
            A workspace, a publication, and your Anthropic key. That is all we
            need before your first piece runs through the pipeline.
          </p>
          <cite>Onboarding · nagareru</cite>
        </div>
      </div>
    </div>
  );
}

// ---------- Sub-components ----------

interface StepperProps {
  current: StepIndex;
  labels: ReadonlyArray<string>;
}

function Stepper({ current, labels }: StepperProps) {
  return (
    <ol className="onboard-stepper" aria-label="Onboarding progress">
      {labels.map((label, idx) => {
        const state =
          idx < current ? "done" : idx === current ? "current" : "upcoming";
        return (
          <li key={label} className={`onboard-step is-${state}`}>
            <span className="onboard-step-dot" aria-hidden>
              {idx < current ? "✓" : idx + 1}
            </span>
            <span className="onboard-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

interface WelcomeStepProps {
  organizationLoaded: boolean;
  hasOrganization: boolean;
  onContinue: () => void;
  canContinue: boolean;
}

function WelcomeStep({
  organizationLoaded,
  hasOrganization,
  onContinue,
  canContinue,
}: WelcomeStepProps) {
  return (
    <>
      <h1 className="login-title">Welcome to nagareru</h1>
      <p className="login-sub">
        Four short steps and you will be ready to ship your first piece.
        We will create your workspace, your first publication, and store
        your Anthropic key.
      </p>

      {organizationLoaded && !hasOrganization && (
        <div className="onboard-org-block">
          <p className="onboard-org-copy">
            nagareru groups every tenant under a Clerk organization. Pick a
            name for yours — it becomes your agency record. You can rename
            it later in account settings.
          </p>
          <CreateOrganization
            afterCreateOrganizationUrl="/onboarding"
            skipInvitationScreen
          />
        </div>
      )}

      <div className="onboard-actions">
        <button
          type="button"
          className="login-submit"
          onClick={onContinue}
          disabled={!canContinue}
        >
          Get started
        </button>
      </div>
    </>
  );
}

interface WorkspaceStepProps {
  value: string;
  onChange: (v: string) => void;
  onContinue: () => void;
  onBack: () => void;
  canContinue: boolean;
}

function WorkspaceStep({
  value,
  onChange,
  onContinue,
  onBack,
  canContinue,
}: WorkspaceStepProps) {
  return (
    <>
      <h1 className="login-title">Name your workspace</h1>
      <p className="login-sub">
        A workspace holds one client engagement. Most agencies create one
        per customer. You can add more from settings later.
      </p>
      <form
        className="login-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canContinue) onContinue();
        }}
      >
        <label className="login-field">
          <span className="login-label">Workspace name</span>
          <input
            className="login-input"
            type="text"
            placeholder="e.g. Acme Manufacturing"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoFocus
          />
        </label>
        <div className="onboard-actions">
          <button type="button" className="onboard-back" onClick={onBack}>
            Back
          </button>
          <button
            type="submit"
            className="login-submit"
            disabled={!canContinue}
          >
            Continue
          </button>
        </div>
      </form>
    </>
  );
}

interface PublicationStepProps {
  value: string;
  onChange: (v: string) => void;
  onContinue: () => void;
  onBack: () => void;
  canContinue: boolean;
}

function PublicationStep({
  value,
  onChange,
  onContinue,
  onBack,
  canContinue,
}: PublicationStepProps) {
  return (
    <>
      <h1 className="login-title">Create your first publication</h1>
      <p className="login-sub">
        A publication is the editorial identity your pieces ship under — a
        blog, a newsletter, a podcast. You can add voice and brand details
        later; we will start with defaults.
      </p>
      <form
        className="login-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canContinue) onContinue();
        }}
      >
        <label className="login-field">
          <span className="login-label">Publication name</span>
          <input
            className="login-input"
            type="text"
            placeholder="e.g. The Acme Quarterly"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoFocus
          />
        </label>
        <div className="onboard-actions">
          <button type="button" className="onboard-back" onClick={onBack}>
            Back
          </button>
          <button
            type="submit"
            className="login-submit"
            disabled={!canContinue}
          >
            Continue
          </button>
        </div>
      </form>
    </>
  );
}

interface ApiKeyStepProps {
  value: string;
  onChange: (v: string) => void;
  skip: boolean;
  onToggleSkip: () => void;
  onBack: () => void;
  onFinish: () => void;
  canFinish: boolean;
  submitting: boolean;
  error: string | null;
  progress: { workspace: boolean; publication: boolean; apiKey: boolean };
}

function ApiKeyStep({
  value,
  onChange,
  skip,
  onToggleSkip,
  onBack,
  onFinish,
  canFinish,
  submitting,
  error,
  progress,
}: ApiKeyStepProps) {
  const showProgress = useMemo(
    () => progress.workspace || progress.publication || progress.apiKey,
    [progress]
  );
  return (
    <>
      <h1 className="login-title">Add your Anthropic key</h1>
      <p className="login-sub">
        Bring your own key — the pipeline calls Claude with your credentials,
        not ours.
      </p>

      {/* S-377, the substantive change on this screen. The wizard creates a
          "Default voice" and a "Default brand" with nothing in them, because
          a publication cannot exist without both. That has always happened
          silently; saying it here is the point. Verified against the code
          that does it, in runProvisioning above. */}
      <div className="onboard-honesty">
        <span className="onboard-honesty-label">Two things we make for you</span>
        <p>
          A publication needs a <strong>voice profile</strong> and a{" "}
          <strong>brand identity</strong> to exist, so we create one of each,
          empty. Your first pieces will read and look generic until you fill
          them in — that is the single largest cause of output you don't
          recognise. Both are waiting under Settings.
        </p>
      </div>
      <form
        className="login-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canFinish) onFinish();
        }}
      >
        <label className="login-field">
          <span className="login-label">Anthropic API key</span>
          <input
            className="login-input"
            type="password"
            placeholder="sk-ant-..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={skip}
            autoFocus={!skip}
            spellCheck={false}
            autoComplete="off"
          />
        </label>

        <label className="onboard-skip">
          <input
            type="checkbox"
            checked={skip}
            onChange={onToggleSkip}
          />
          <span>Skip for now — I will add it from Settings later</span>
        </label>
        {/* The same honesty for the key: what you land in, not what you can
            do later. */}
        {skip && (
          <div className="onboard-honesty">
            <span className="onboard-honesty-label">Without a key</span>
            <p>
              You will land in a finished workspace with a pipeline that cannot
              run: pieces can be created, but no agent will start until a key is
              stored under Settings → API keys.
            </p>
          </div>
        )}

        {showProgress && (
          <ul className="onboard-progress" aria-live="polite">
            <li className={progress.workspace ? "is-done" : ""}>
              Workspace {progress.workspace ? "created" : "pending"}
            </li>
            <li className={progress.publication ? "is-done" : ""}>
              Publication {progress.publication ? "created" : "pending"}
            </li>
            {!skip && (
              <li className={progress.apiKey ? "is-done" : ""}>
                API key {progress.apiKey ? "stored" : "pending"}
              </li>
            )}
          </ul>
        )}

        {error && (
          <div className="onboard-error" role="alert">
            <strong>Provisioning failed.</strong> {error}
            <div className="onboard-error-hint">
              Your inputs are preserved — press Finish to retry; any step
              that already succeeded will be skipped.
            </div>
          </div>
        )}

        <div className="onboard-actions">
          <button
            type="button"
            className="onboard-back"
            onClick={onBack}
            disabled={submitting}
          >
            Back
          </button>
          <button
            type="submit"
            className="login-submit"
            disabled={!canFinish}
          >
            {submitting
              ? "Setting up…"
              : skip
              ? "Finish without a key"
              : "Finish"}
          </button>
        </div>
      </form>
    </>
  );
}
